import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Organization, OrganizationList, OrganizationState, PatchOperation, SearchResponse } from '@app/_store';
import { Store } from '@ngxs/store';
import {
  ChangeMemberGroupResponse,
  GetTransactionsResponse,
  Member,
  MemberImportResponse,
  RetiredFile,
  UploadOrganizationLogoResponse,
} from './organization-api';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  constructor(
    private http: HttpClient,
    private store: Store
  ) {}

  registerOrganization(payload: {
    name: string;
    contactPhone: string;
    parent?: { id: string };
    isSupervisor: boolean;
  }): Observable<{ id: string; registered: boolean; token: string }> {
    return this.http.post<{ id: string; registered: boolean; token: string }>(
      `${environment.apiUrl}/organizations/registrations`,
      payload
    );
  }

  getAll(): Observable<OrganizationList> {
    return this.http.get<OrganizationList>(`${environment.apiUrl}/organizations`);
  }

  getOverview(id: string): Observable<Organization> {
    return this.http.get<Organization>(`${environment.apiUrl}/organizations/${id}`);
  }

  getMembers(id: string): Observable<{ members: Member[] }> {
    return this.http.get<{ members: Member[] }>(`${environment.apiUrl}/organizations/${id}/members`);
  }

  deleteAdministrator(userId: string): Observable<void> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.delete<void>(`${environment.apiUrl}/organizations/${currentOrganization.id}/administrators/${userId}`);
  }

  denyAdministratorRequest(userId: string): Observable<void> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.delete<void>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/administrators/requests/${userId}`
    );
  }

  approveAdministratorRequest(userId: string): Observable<void> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.put<void>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/administrators/requests/${userId}`,
      null
    );
  }

  postponeImportReminder(): Observable<string> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.delete<string>(`${environment.apiUrl}/organizations/${currentOrganization.id}/members/imports/reminders`);
  }

  search(term: string): Observable<SearchResponse> {
    return this.http.get<SearchResponse>(`${environment.apiUrl}/organizations/search?searchTerm=${term}`);
  }

  patchOrganization(operations: PatchOperation[]): Observable<void> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.patch<void>(`${environment.apiUrl}/organizations/${currentOrganization.id}`, operations);
  }

  uploadLogo(file: File): Observable<UploadOrganizationLogoResponse> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const formData: FormData = new FormData();
    formData.append('file', file);

    return this.http.post<UploadOrganizationLogoResponse>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/logo`,
      formData
    );
  }

  importMembers(data: FormData, commit: boolean): Observable<MemberImportResponse> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.post<MemberImportResponse>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/members/imports?commit=${commit}`,
      data
    );
  }

  updateMember(memberId: string, operations: PatchOperation[]): Observable<Member> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.patch<Member>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/members/${memberId}`,
      operations
    );
  }

  changeMemberGroup(memberId: string, groupId: string): Observable<ChangeMemberGroupResponse> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.put<ChangeMemberGroupResponse>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/members/${memberId}/group/${groupId}`,
      null
    );
  }

  requestAdministratorPrivileges(organizationId: string): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/organizations/${organizationId}/administrators/requests`, null);
  }

  retire(): Observable<{ removeDate: Date }> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.delete<{ removeDate: Date }>(`${environment.apiUrl}/organizations/${currentOrganization.id}`);
  }

  getRetiredDownloads(): Observable<RetiredFile[]> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.get<RetiredFile[]>(`${environment.apiUrl}/organizations/${currentOrganization.id}/retired-downloads`);
  }

  getRetiredDownloadsZip() {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.get<BlobPart>(`${environment.apiUrl}/organizations/${currentOrganization.id}/retired-downloads/zip`, {
      responseType: 'blob' as 'json',
    });
  }

  addWallet(memberId: string, initialBalance: number) {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.post(`${environment.apiUrl}/organizations/${currentOrganization.id}/members/${memberId}/wallet`, {
      initialBalance: initialBalance,
    });
  }

  getTransactions(memberId: string) {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.get<GetTransactionsResponse>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/members/${memberId}/wallet/transactions`
    );
  }
}
